import type { Appointment } from '@clinic-os/shared/types';
import { DEMO_APPOINTMENTS, DEMO_CLINIC_ID } from '../data/demo-data';

export interface AppointmentService {
  getAll: (clinicId?: string) => Promise<Appointment[]>;
  getById: (id: string) => Promise<Appointment | null>;
  getByDate: (date: Date, clinicId?: string) => Promise<Appointment[]>;
  getByPatient: (patientId: string) => Promise<Appointment[]>;
  create: (data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Appointment>;
  update: (id: string, data: Partial<Appointment>) => Promise<Appointment>;
  cancel: (id: string) => Promise<Appointment>;
  getUpcoming: (clinicId?: string, limit?: number) => Promise<Appointment[]>;
}

class AppointmentServiceImpl implements AppointmentService {
  private appointments: Appointment[] = [...DEMO_APPOINTMENTS];

  async getAll(clinicId = DEMO_CLINIC_ID): Promise<Appointment[]> {
    return this.appointments.filter((a) => a.clinicId === clinicId);
  }

  async getById(id: string): Promise<Appointment | null> {
    return this.appointments.find((a) => a.id === id) ?? null;
  }

  async getByDate(date: Date, clinicId = DEMO_CLINIC_ID): Promise<Appointment[]> {
    return this.appointments.filter(
      (a) =>
        a.clinicId === clinicId &&
        new Date(a.scheduledAt).toDateString() === date.toDateString()
    );
  }

  async getByPatient(patientId: string): Promise<Appointment[]> {
    return this.appointments.filter((a) => a.patientId === patientId);
  }

  async create(data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Appointment> {
    const now = new Date().toISOString();
    const apt: Appointment = {
      ...data,
      id: `a${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    this.appointments.push(apt);
    return apt;
  }

  async update(id: string, data: Partial<Appointment>): Promise<Appointment> {
    const idx = this.appointments.findIndex((a) => a.id === id);
    if (idx === -1) throw new Error(`Appointment ${id} not found`);
    const updated: Appointment = { ...this.appointments[idx]!, ...data, updatedAt: new Date().toISOString() };
    this.appointments[idx] = updated;
    return updated;
  }

  async cancel(id: string): Promise<Appointment> {
    return this.update(id, { status: 'cancelled' });
  }

  async getUpcoming(clinicId = DEMO_CLINIC_ID, limit = 10): Promise<Appointment[]> {
    const now = new Date().toISOString();
    return this.appointments
      .filter(
        (a) =>
          a.clinicId === clinicId &&
          new Date(a.scheduledAt) >= new Date(now) &&
          a.status !== 'cancelled'
      )
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
      .slice(0, limit);
  }
}

export const appointmentService = new AppointmentServiceImpl();
